<script setup lang="ts">
import { computed } from "vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

export type BadgeColor = "primary" | "green" | "teal" | "indigo" | "blue" | "purple" | "red" | "yellow" | "gray";

export interface Badge {
  color: BadgeColor,
  text?: string,
  statusIcon?: IconDefinition,
  togglable?: boolean,
  modelValue?: boolean
}
const props = withDefaults(defineProps<Badge>(), {
  statusIcon: undefined,
  text: "",
  togglable: false,
  modelValue: false
});

const emit = defineEmits(["update:modelValue"]);

const iconOnly = computed(() => {
  return !!props.statusIcon && !props.text;
});

function toggle() {
  if (props.togglable) {
    emit("update:modelValue", !props.modelValue);
  }
}
</script>

<template>
  <span 
    :class="['badge', props.color, {
      'icon-only': iconOnly,
      'togglable': props.togglable,
      'active': props.togglable && props.modelValue
    }]"
    @click="toggle"
  >
    <span v-if="props.statusIcon" class="badge-icon">
      <FontAwesomeIcon v-if="props.statusIcon" :icon="props.statusIcon" />
    </span>
    {{ props.text }}
  </span>
</template>

<style lang="scss" scoped>
$themes: (
  "green": (
    bg: map.get(v.$success, "50"),
    icon: map.get(v.$success, "500"),
    text: map.get(v.$success, "700")
  ),
  "teal": (
    bg: map.get(v.$info, "50"),
    icon: map.get(v.$info, "500"),
    text: map.get(v.$info, "700")
  ),
  "indigo": (
    bg: #E2E9F7,
    icon: #7d7ff6,
    text: #3538CD
  ),
  "blue": (
    bg: #F0F9FF,
    icon: #58BDF3,
    text: #026AA2
  ),
  "purple": (
    bg: #F4F3FF,
    icon: #9092E2,
    text: #5925DC
  ),
  "primary": (
    bg: map.get(v.$primary, "50"),
    icon: map.get(v.$primary, "400"),
    text: map.get(v.$primary, "600")
  ),
  "red": (
    bg: map.get(v.$error, "50"),
    icon: map.get(v.$error, "400"),
    text: map.get(v.$error, "700")
  ),
  "yellow": (
    bg: map.get(v.$warning, "50"),
    icon: map.get(v.$warning, "400"),
    text: map.get(v.$warning, "700")
  ),
  "gray": (
    bg: map.get(v.$gray, "100"),
    icon: map.get(v.$gray, "400"),
    text: map.get(v.$gray, "500")
  )
);

@each $badgeColor, $colors in $themes {
  .#{$badgeColor}.badge {
    background-color: map.get($colors, "bg");
    color: map.get($colors, "text");
    .badge-icon {
      color: map.get($colors, "icon");
    }
    &.togglable.active {
      border-color: map.get($colors, "icon");
    }
  }
}

.badge {
  display: inline-flex;
  gap: 5px;
  padding: .25em .75em;
  font-size: .75rem;
  font-weight: map.get(v.$font-weight, "medium");
  border-radius: map.get(v.$border-radius, "max");
  text-wrap: nowrap;
  &.icon-only {
    padding: .25em;
    height: 20px;
    width: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  &.togglable {
    user-select: none;
    cursor: pointer;
    transition: 0.2s ease;
    border: 1px solid transparent;
    &:hover {
      filter: brightness(0.95) saturate(1.8);
    }
  }
}
</style>
